import { DataTable, FormGroupHeader, IndicatorNumber } from "@/components/shared";
import {
  CarDetails,
  DecisionEnum,
  MarginDecisionEnum,
  MarginDecisionGroupPermissionEnum,
  PurchaseDetails,
  Service,
  ServiceType,
  TenderPermissionEnum,
} from "@/graphql/__generated__/graphql";
import { getColumnsServices } from "../columns";
import { getColumnsCars } from "../Cars/columns";
import { getColumnsPurchases } from "../Purchases/columns";
import { useIntl } from "react-intl";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useRemoveServiceMutationHook, useRequestMarginDecisionOnGroupMutationHook, useRemoveCarMutationHook, useRemovePurchaseMutationHook } from "@/hooks";
import { ConfirmRemoveAlert } from "../Dialog";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { useState, useEffect } from "react";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useTranslation } from "react-i18next";
import { useFetchCarsOnTenderQueryHook, useFetchPurchasesOnTenderQueryHook, useFetchServicesOnTenderQueryHook } from "@/hooks/queries";
import MarginDecisionAlertDialog from "../Components/TenderSideNavigation/MarginDecisionAlertDialog";
import { appendOpOrgIdParams } from "@/utils/helpers";
import DecisionConfirmationAlertDialog from "../MarginDecisions/Components/DecisionConfirmationAlertDialog";
import { PiListChecks, PiCar, PiShoppingCart } from "react-icons/pi";
import AddCar from "../AddCar/Form";
import AddPurchase from "../AddPurchase/Form";
import EditCarDialog from "../Cars/EditCarDialog";
import EditPurchaseDialog from "../Purchases/EditPurchaseDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { openCalculationOverviewDialog } from "@/store/calculationOverviewDialog";

const Services = () => {

  // Hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { tenderID: currentTenderID } = useParams();
  const [searchParams] = useSearchParams();
  const currentOperationalOrganizationID = searchParams.get("operationalOrganizationId");
  const { tenderByIdData, canAddService } = useOutletContext<EditTenderContext>();
  const { t } = useTranslation(["translation", "common", "tables", "components"]);

  const marginDecisionGroups = tenderByIdData?.fetchTenderById?.marginDecisionGroups;

  // Query hooks
  const { data, loading, refetch } = useFetchServicesOnTenderQueryHook(Number(currentTenderID), false, true);
  const { data: carsData, loading: carsLoading } = useFetchCarsOnTenderQueryHook(Number(currentTenderID));
  const { data: purchasesData, loading: purchasesLoading } = useFetchPurchasesOnTenderQueryHook(Number(currentTenderID));

  // Mutation hooks for cars and purchases
  const { removeCarMutation, loading: removeCarLoading } = useRemoveCarMutationHook();
  const { removePurchaseMutation, loading: removePurchaseLoading } = useRemovePurchaseMutationHook();

  // States
  const [currentServiceID, setCurrentServiceID] = useState<number | undefined>();
  const [showConfirmRemoveAlert, setShowConfirmRemoveAlert] = useState<boolean>(false);
  const [openRequestApprovalDialog, setOpenRequestApprovalDialog] = useState<boolean>(false);
  const [rerequestConfirmationOpen, setRerequestConfirmationOpen] = useState<boolean>(false);
  const [marginDecisionGroupID, setMarginDecisionGroupID] = useState<number | undefined>();

  // Cars and Purchases states
  const [currentCarDetails, setCurrentCarDetails] = useState<CarDetails | null>(null);
  const [currentPurchaseDetails, setCurrentPurchaseDetails] = useState<PurchaseDetails | null>(null);
  const [showAddCarModal, setShowAddCarModal] = useState<boolean>(false);
  const [showAddPurchaseModal, setShowAddPurchaseModal] = useState<boolean>(false);
  const [showEditCarModal, setShowEditCarModal] = useState<boolean>(false);
  const [showEditPurchaseModal, setShowEditPurchaseModal] = useState<boolean>(false);
  const [showConfirmRemoveCarAlert, setShowConfirmRemoveCarAlert] = useState<boolean>(false);
  const [showConfirmRemovePurchaseAlert, setShowConfirmRemovePurchaseAlert] = useState<boolean>(false);

  // Cars and Purchases data
  const allCarDetails = (carsData?.fetchCarsOnTender ?? []) as CarDetails[];
  const allPurchaseDetails = (purchasesData?.fetchPurchasesOnTender ?? []) as PurchaseDetails[];
  const allServices = data?.fetchServicesOnTender?.services ?? [];
  const canAddCar = tenderByIdData?.fetchTenderById?.permissions.includes(TenderPermissionEnum.AddService);
  const canAddPurchase = tenderByIdData?.fetchTenderById?.permissions.includes(TenderPermissionEnum.AddService);

  // Effect to set the margin decision group ID based on the current operational organization ID
  useEffect(() => {
    if (marginDecisionGroups && currentOperationalOrganizationID) {
      const group = marginDecisionGroups.find(
        (group) => group.operationalOrganizationId === Number(currentOperationalOrganizationID)
      );
      setMarginDecisionGroupID(group?.id);
    } else {
      setMarginDecisionGroupID(undefined);
    }
  }, [marginDecisionGroups, currentOperationalOrganizationID]);

  // Constants
  const canRequestApproval = tenderByIdData?.fetchTenderById?.permissions.includes(TenderPermissionEnum.RequestMarginDecision);
  const tenderServices = allServices.filter((service) => {
    // If there's no operationalOrganizationId, include all services
    if (!currentOperationalOrganizationID) {
      return true;
    }

    // Otherwise, only include services that match the specified ID
    return service.operationalOrganizationId === Number(currentOperationalOrganizationID);
  });

  const tenderServiceIdSet = new Set(tenderServices.map((service) => service.id));
  const carDetails = currentOperationalOrganizationID
    ? allCarDetails.filter((car) => (car.links ?? []).some((link) => tenderServiceIdSet.has(link.serviceId)))
    : allCarDetails;
  const purchaseDetails = currentOperationalOrganizationID
    ? allPurchaseDetails.filter((purchase) => (purchase.links ?? []).some((link) => tenderServiceIdSet.has(link.serviceId)))
    : allPurchaseDetails;

  const includedServices = tenderServices.filter((service) => !service.additional);
  const additionalServices = tenderServices.filter((service) => service.additional);

  const bidDecisionGroups = tenderByIdData?.fetchTenderById?.bidDecisionGroups ?? [];
  const biddingOrganizations = bidDecisionGroups.filter((group) => group.currentDecision === DecisionEnum.Bid);

  // get the current organization's short name
  const currentOrganization = biddingOrganizations.find((group) =>
    group.operationalOrganizationId === Number(currentOperationalOrganizationID)
  );

  // Mutation hooks
  const { requestMarginDecisionOnGroupMutation, loading: requestLoading } = useRequestMarginDecisionOnGroupMutationHook();
  const { removeServiceMutation } = useRemoveServiceMutationHook(Number(currentServiceID));

  /**
   * Handles the submission for re-requesting a margin decision on the group.
   */
  const onRequestClick = () => {
    if (!marginDecisionGroupID) return;

    try {
      requestMarginDecisionOnGroupMutation({
        variables: {
          groupId: Number(marginDecisionGroupID),
        }
      }).then(() => {
        // Refetch the services
        refetch();
      });
    }
    catch (err) {
      console.error(err);
    }
    finally {
      // Close the re-request confirmation dialog
      setRerequestConfirmationOpen(false);
    }
  };

  /**
   * OnClick handler for the Details button in the Services table
   *
   * @param {Service} service - The service to view details for
   */
  const handleOnClickDetails = (service: Service) => {
    openCalculationOverviewDialog(service.id, currentOperationalOrganizationID);
  };

  /**
   * OnClick handler for the Remove button in the Services table
   *
   * @param {Service} service - The service to be removed
   */
  const handleOnClickRemove = (service: Service) => {
    setCurrentServiceID(service.id);
    setShowConfirmRemoveAlert(true);
  };

  /**
   * OnClick handler for the Edit button in the Services table.
   * Navigates to the edit page for a given service while preserving existing query parameters.
   *
   * @param {Service} service - The service to be edited.
   *
   * @returns {void}
   */
  const handleOnClickEdit = (service: Service): void => {
    let basePath: any;
    if (service.type === ServiceType.Permanent) {
      basePath = `/tender/${currentTenderID}/services/permanent/${service.id}${appendOpOrgIdParams(currentOperationalOrganizationID)}`;
    } else if (service.type === ServiceType.Mobile) {
      basePath = `/tender/${currentTenderID}/services/mobile/${service.id}${appendOpOrgIdParams(currentOperationalOrganizationID)}`;
    }

    navigate(basePath);
  };

  /**
   * Handler function for `removeServiceMutation`
   */
  const handleRemoveService = () => {
    const serviceBeingRemoved = allServices.find((service) => service.id === currentServiceID);
    const shouldRefetchCars = (serviceBeingRemoved?.cars ?? []).length > 0;
    const shouldRefetchPurchases = (serviceBeingRemoved?.purchases ?? []).length > 0;

    removeServiceMutation(
      {
        serviceId: Number(currentServiceID),
      },
      {
        refetchCars: shouldRefetchCars,
        refetchPurchases: shouldRefetchPurchases,
      }
    ).then(() => {
      setCurrentServiceID(undefined);
      setShowConfirmRemoveAlert(false);
    });
  };

  // Car handlers
  /**
   * Handler for clicking the edit button on a car row
   * @param {CarDetails} carDetails - The car details to be edited
   */
  const handleOnClickEditCar = (carDetails: CarDetails) => {
    setCurrentCarDetails(carDetails);
    setShowEditCarModal(true);
  };

  /**
   * Handler for clicking the remove button on a car row
   * @param {CarDetails} carDetails - The car details to be removed
   */
  const handleOnClickRemoveCar = (carDetails: CarDetails) => {
    setCurrentCarDetails(carDetails);
    setShowConfirmRemoveCarAlert(true);
  };

  /**
   * Handler for removing a car
   */
  const handleRemoveCar = () => {
    if (!currentCarDetails) return;

    removeCarMutation({
      variables: {
        tenderId: Number(currentTenderID),
        carId: currentCarDetails.car.id,
      },
    });
    setShowConfirmRemoveCarAlert(false);
    setCurrentCarDetails(null);
  };

  // Purchase handlers
  /**
   * Handler for clicking the edit button on a purchase row
   * @param {PurchaseDetails} purchaseDetails - The purchase details to be edited
   */
  const handleOnClickEditPurchase = (purchaseDetails: PurchaseDetails) => {
    setCurrentPurchaseDetails(purchaseDetails);
    setShowEditPurchaseModal(true);
  };

  /**
   * Handler for clicking the remove button on a purchase row
   * @param {PurchaseDetails} purchaseDetails - The purchase details to be removed
   */
  const handleOnClickRemovePurchase = (purchaseDetails: PurchaseDetails) => {
    setCurrentPurchaseDetails(purchaseDetails);
    setShowConfirmRemovePurchaseAlert(true);
  };

  /**
   * Handler for removing a purchase
   */
  const handleRemovePurchase = () => {
    if (!currentPurchaseDetails) return;

    removePurchaseMutation({
      variables: {
        tenderId: Number(currentTenderID),
        purchaseId: currentPurchaseDetails.purchase.id,
      },
    });
    setShowConfirmRemovePurchaseAlert(false);
    setCurrentPurchaseDetails(null);
  };

  /**
   * Returns the re-request button for the services table.
   *
   * @returns {ActionButtonConfig | null} - The re-request button for the services table.
   */
  const getRerequestButton = () => {
    // Check if there are any re-request groups, rerequest permissions for users sits in the margin decision group.
    const reRequestGroups = marginDecisionGroups?.filter((group) => group.permissions.includes(MarginDecisionGroupPermissionEnum.Rerequest)) ?? [];

    // If there are no re-request groups, return null
    if (reRequestGroups.length === 0) {
      return null;
    }

    // If there is no operational organization ID, return the re-request button with dropdown links. This is for all services page.
    if (!currentOperationalOrganizationID) {
      return {
        text: t("common:buttons.rerequest"),
        variant: "outline",
        icon: <PiListChecks className="icon-sm" />,
        dropdownLinks: reRequestGroups.map((group) => ({
          text: group.operationalOrganizationShortName,
          actionFn: () => {
            setMarginDecisionGroupID(group.id);
            setRerequestConfirmationOpen(true);
          },
        })),
      };
    }

    // If there is an operational organization ID, meaning it is in a specific organization's service page,
    // find the re-request group for the current organization and return the re-request button.
    if (reRequestGroups.find((group) => group.operationalOrganizationId === Number(currentOperationalOrganizationID))) {
      return {
        text: requestLoading ? t("common:buttons.submitting") : t("common:buttons.rerequest"),
        variant: "outline",
        className: "group",
        actionFn: () => setRerequestConfirmationOpen(true),
        icon: <PiListChecks className="icon-sm" />
      };
    }
  };

  // Action buttons for FormGroupHeader, including dropdown logic with canRerequest, canRequestApproval and canAddService checks
  const actionButtons: ActionButtonConfig[] = ([
    (getRerequestButton()),
    (canRequestApproval) &&
    {
      text: t("edittender.navlinks.progress.requestapproval"),
      variant: "outline",
      icon: <PiListChecks className="icon-sm" />,
      actionFn: () => setOpenRequestApprovalDialog(true),
    },
    (canAddCar) &&
    {
      text: t("edittender.cars.addcar"),
      variant: "outline",
      icon: <PiCar className="icon-sm" />,
      actionFn: () => setShowAddCarModal(true),
    },
    (canAddPurchase) &&
    {
      text: t("edittender.purchases.addpurchase"),
      variant: "outline",
      icon: <PiShoppingCart className="icon-sm" />,
      actionFn: () => setShowAddPurchaseModal(true),
    },
    (canAddService) &&
    {
      text: t("edittender.services.btnnewservice"),
      dropdownLinks: [
        {
          text: t("edittender.services.btnpermanentservice"),
          actionFn: () => navigate(`/tender/${currentTenderID}/services/permanent/new${appendOpOrgIdParams(currentOperationalOrganizationID)}`),
        },
        {
          text: t("edittender.services.btnmobileservice"),
          actionFn: () => navigate(`/tender/${currentTenderID}/services/mobile/new${appendOpOrgIdParams(currentOperationalOrganizationID)}`),
        },
      ],
    },
  ] as ActionButtonConfig[]).filter(Boolean);

  return (
    <>
      <FormGroupHeader
        actionButtons={actionButtons}
        title={`${currentOrganization ? currentOrganization?.operationalOrganizationShortName : t("edittender.services.all")} ${t("edittender.services.title")}`}
        description={t("edittender.services.description")}
        className="pr-4 pl-2"
      />

      <div className="pr-4 pl-2 pb-[300px]">
        <Accordion type="multiple" defaultValue={["services", "cars", "purchases"]} className="w-full">
          {/* Services Section */}
          <AccordionItem value="services" className="border rounded-lg mb-4 px-4 last:border-b">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{t("edittender.services.title")}</h2>
                <IndicatorNumber number={tenderServices.length} />
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {/* Included Services table */}
              <DataTable
                disableRowColour={true}
                loading={loading}
                displayAmount={includedServices.length !== 0 ? includedServices.length : 5}
                data={(includedServices ?? []) as unknown as Service[]}
                columns={getColumnsServices(intl, t, handleOnClickRemove, handleOnClickEdit, handleOnClickDetails)}
                className="max-h-[60vh]"
                enableColumnPinning={true}
                columnPinning={{ left: [], right: ["actions"] }}
              />

              {/* Separator between included and additional services */}
              {additionalServices.length > 0 && (
                <>
                  <div className="my-4 flex items-center gap-3">
                    <Separator className="flex-1" />
                    <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                      {t("edittender.services.additional")}
                    </span>
                    <Separator className="flex-1" />
                  </div>

                  {/* Additional Services table */}
                  <DataTable
                    disableRowColour={true}
                    loading={loading}
                    displayAmount={additionalServices.length}
                    data={(additionalServices ?? []) as unknown as Service[]}
                    columns={getColumnsServices(intl, t, handleOnClickRemove, handleOnClickEdit, handleOnClickDetails)}
                    className="max-h-[60vh]"
                    enableColumnPinning={true}
                    columnPinning={{ left: [], right: ["actions"] }}
                  />
                </>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Cars Section */}
          <AccordionItem value="cars" className="border rounded-lg mb-4 px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{t("edittender.cars.title")}</h2>
                <IndicatorNumber number={carDetails.length} />
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="mb-4 text-sm text-muted-foreground max-w-2xl">
                {t("edittender.cars.description")}
              </p>
              <DataTable
                disableRowColour={true}
                loading={carsLoading}
                displayAmount={carDetails.length !== 0 ? carDetails.length : 3}
                data={carDetails}
                columns={getColumnsCars(intl, t, !!canAddCar, handleOnClickEditCar, handleOnClickRemoveCar, removeCarLoading, allServices)}
                className="max-h-[40vh]"
                enableColumnPinning={true}
                columnPinning={{ left: [], right: ["actions"] }}
              />
            </AccordionContent>
          </AccordionItem>

          {/* Purchases Section */}
          <AccordionItem value="purchases" className="border rounded-lg mb-4 px-4 last:border-b">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{t("edittender.purchases.title")}</h2>
                <IndicatorNumber number={purchaseDetails.length} />
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <p className="mb-4 text-sm text-muted-foreground max-w-2xl">
                {t("edittender.purchases.description")}
              </p>
              <DataTable
                disableRowColour={true}
                loading={purchasesLoading}
                displayAmount={purchaseDetails.length !== 0 ? purchaseDetails.length : 3}
                data={purchaseDetails}
                columns={getColumnsPurchases(intl, t, !!canAddPurchase, handleOnClickEditPurchase, handleOnClickRemovePurchase, removePurchaseLoading, allServices)}
                className="max-h-[40vh]"
                enableColumnPinning={true}
                columnPinning={{ left: [], right: ["actions"] }}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Confirm Remove Service Alert */}
      <ConfirmRemoveAlert
        open={showConfirmRemoveAlert}
        onOpenChange={setShowConfirmRemoveAlert}
        actionButtonFn={handleRemoveService}
        bodyText={t("components:alertdialogs.removeservice")}
      />

      <MarginDecisionAlertDialog
        services={allServices as Service[]}
        biddingGroups={bidDecisionGroups}
        open={openRequestApprovalDialog}
        onOpenChange={setOpenRequestApprovalDialog}
      />

      <DecisionConfirmationAlertDialog
        open={rerequestConfirmationOpen}
        onOpenChange={setRerequestConfirmationOpen}
        userDecision={MarginDecisionEnum.Request}
        loading={requestLoading}
        actionFn={onRequestClick}
      />

      {/* Cars Dialogs - Conditionally rendered to avoid unnecessary queries */}
      {showAddCarModal && <AddCar open={showAddCarModal} onOpenChange={setShowAddCarModal} />}
      {showEditCarModal && (
        <EditCarDialog
          open={showEditCarModal}
          onOpenChange={setShowEditCarModal}
          carDetails={currentCarDetails}
        />
      )}
      <ConfirmRemoveAlert
        open={showConfirmRemoveCarAlert}
        onOpenChange={setShowConfirmRemoveCarAlert}
        actionButtonFn={handleRemoveCar}
        bodyText={t("components:alertdialogs.removecar")}
      />

      {/* Purchases Dialogs - Conditionally rendered to avoid unnecessary queries */}
      {showAddPurchaseModal && <AddPurchase open={showAddPurchaseModal} onOpenChange={setShowAddPurchaseModal} />}
      {showEditPurchaseModal && (
        <EditPurchaseDialog
          open={showEditPurchaseModal}
          onOpenChange={setShowEditPurchaseModal}
          purchaseDetails={currentPurchaseDetails}
        />
      )}
      <ConfirmRemoveAlert
        open={showConfirmRemovePurchaseAlert}
        onOpenChange={setShowConfirmRemovePurchaseAlert}
        actionButtonFn={handleRemovePurchase}
        bodyText={t("components:alertdialogs.removepurchase")}
      />
    </>
  );
};

export default Services;
